import React, { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { animate, stagger } from "motion";
import {
  Upload,
  MessageSquareText,
  Send,
  CheckCircle,
  ChevronRight,
  ArrowRight,
  Database,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const HowItWorks = ({ imageUrl }) => {
  const navigate = useNavigate();
  const [sectionRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [stepsRef, stepsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [mobileStepsRef, mobileStepsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [imageRef, imageInView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  useEffect(() => {
    if (inView) {
      animate(
        ".title-animation",
        { y: [50, 0], opacity: [0, 1] },
        { duration: 0.6, delay: stagger(0.2) }
      );
    }
  }, [inView]);

  useEffect(() => {
    if (stepsInView) {
      animate(
        ".desktop-step-card",
        {
          opacity: [0, 1],
          scale: [0.9, 1],
          y: [30, 0],
        },
        {
          duration: 0.5,
          delay: stagger(0.2),
        }
      );

      animate(
        ".desktop-step-number",
        { opacity: [0, 1], scale: [0.5, 1] },
        { duration: 0.7, delay: stagger(0.2, { start: 0.3 }) }
      );

      animate(
        ".desktop-step-line",
        { scaleY: [0, 1], opacity: [0, 1] },
        { duration: 0.8, delay: stagger(0.2, { start: 0.2 }) }
      );
    }
  }, [stepsInView]);

  useEffect(() => {
    if (mobileStepsInView) {
      animate(
        ".mobile-step-card",
        {
          opacity: [0, 1],
          scale: [0.9, 1],
          y: [30, 0],
        },
        {
          duration: 0.5,
          delay: stagger(0.2),
        }
      );

      animate(
        ".mobile-step-number",
        { opacity: [0, 1], scale: [0.5, 1] },
        { duration: 0.7, delay: stagger(0.2, { start: 0.3 }) }
      );

      animate(
        ".mobile-step-line",
        { scaleY: [0, 1], opacity: [0, 1] },
        { duration: 0.8, delay: stagger(0.2, { start: 0.2 }) }
      );

      animate(
        ".mobile-button",
        { opacity: [0, 1], y: [20, 0] },
        { duration: 0.5, delay: 0.8 }
      );
    }
  }, [mobileStepsInView]);

  useEffect(() => {
    if (imageInView) {
      animate(
        ".image-animation",
        {
          opacity: [0, 1],
          scale: [0.95, 1],
          x: [-30, 0],
        },
        {
          duration: 0.8,
        }
      );

      animate(
        ".glass-element",
        {
          opacity: [0, 0.8],
          scale: [0.8, 1],
        },
        {
          duration: 0.6,
          delay: 0.3,
        }
      );
    }
  }, [imageInView]);

  const steps = [
    {
      number: "01",
      title: "Upload Your Contact List",
      description:
        "Upload or enter a list of phone numbers, or import contacts via your CRM.",
      icon: <Upload className="w-7 h-7" />,
      iconColor: "text-red-600",
      bgColor: "bg-red-100",
      borderColor: "border-red-200",
      features: [
        { text: "XLSX or XLS format", icon: <Database className="w-4 h-4" /> },
      ],
    },
    {
      number: "02",
      title: "Compose Your SMS",
      description: "Write your SMS message for both quick and bulk messaging.",
      icon: <MessageSquareText className="w-7 h-7" />,
      iconColor: "text-blue-600",
      bgColor: "bg-blue-100",
      borderColor: "border-blue-200",
      features: [
        {
          text: "Character counter",
          icon: <MessageSquareText className="w-4 h-4" />,
        },
        { text: "Template library", icon: <CheckCircle className="w-4 h-4" /> },
      ],
    },
    {
      number: "03",
      title: "Mass Send Your SMS",
      description: "Hit 'send' to send your SMS immediately.",
      icon: <Send className="w-7 h-7" />,
      iconColor: "text-purple-600",
      bgColor: "bg-purple-100",
      borderColor: "border-purple-200",
      features: [
        { text: "Delivery reports", icon: <CheckCircle className="w-4 h-4" /> },
      ],
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="overflow-hidden px-4 py-12 bg-gradient-to-b from-white to-gray-50 sm:py-16 md:py-20 lg:py-24 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-8xl">
        <div className="mb-10 text-center sm:mb-16">
          <h4 className="mb-2 text-base font-medium text-red-600 sm:text-lg title-animation">
            STEPS
          </h4>
          <h2 className="mb-4 text-3xl font-bold leading-tight text-gray-900 sm:text-4xl md:text-5xl sm:mb-6 title-animation">
            How TXTConnect Works <br className="hidden sm:block" />
            <span className="text-red-600">For Bulk SMS.</span>
          </h2>
          <div className="mx-auto w-16 h-1 bg-red-600 sm:w-24 title-animation"></div>
        </div>

        {/* Two column layout with glassmorphic image left and content right */}
        <div className="flex flex-col gap-8 mb-16 lg:flex-row lg:gap-12 md:mb-24">
          {/* Mobile order - steps first on small screens */}
          <div className="block mb-8 w-full lg:hidden">
            {/* Timeline Content - FIXED Mobile Version */}
            <div ref={mobileStepsRef} className="relative w-full">
              {/* Mobile Steps */}
              {steps.map((step, index) => (
                <div key={step.number} className="relative mb-6 last:mb-8">
                  {/* Mobile Step Card */}
                  <div
                    className={`p-4 bg-white rounded-xl border border-gray-100 shadow-lg opacity-0 transition-all duration-300 mobile-step-card hover:shadow-xl`}
                  >
                    <div className="flex items-center mb-3">
                      <div
                        className={`flex-shrink-0 w-12 h-12 rounded-full ${step.bgColor} ${step.borderColor} flex items-center justify-center mr-3 mobile-step-number opacity-0`}
                      >
                        <span className={`text-xl font-bold ${step.iconColor}`}>
                          {step.number}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">
                        {step.title}
                      </h3>
                    </div>

                    <p className="pl-14 mb-4 text-gray-600">
                      {step.description}
                    </p>

                    <div className="pl-14">
                      {/* Icon section */}
                      <div className="flex items-center mb-3">
                        <div
                          className={`p-2 rounded-full ${step.bgColor} ${step.iconColor} mr-3`}
                        >
                          {step.icon}
                        </div>
                      </div>

                      {/* Features list - Mobile */}
                      <div className="p-3 mb-3 space-y-2 bg-gray-50 rounded-lg">
                        {step.features.map((feature, fIndex) => (
                          <div key={fIndex} className="flex items-center">
                            <div
                              className={`w-5 h-5 rounded-full ${step.bgColor} flex items-center justify-center mr-2`}
                            >
                              {feature.icon}
                            </div>
                            <span className="text-xs text-gray-700">
                              {feature.text}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* SMS indicator for step 2 - Mobile */}
                      {index === 1 && (
                        <div className="flex flex-wrap gap-2">
                          <div className="flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-full border border-blue-200">
                            <MessageSquareText className="mr-1 w-3 h-3" />
                            SMS
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Connector line */}
                    {index < steps.length - 1 && (
                      <div className="flex justify-center my-4">
                        <div className="mobile-step-line opacity-0 w-0.5 h-12 bg-gradient-to-b from-red-400 via-blue-400 to-purple-400"></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex justify-center mt-6">
                <button
                  className="flex justify-center items-center px-6 py-3 w-full text-base font-bold text-white bg-gradient-to-r from-red-600 to-red-500 rounded-full shadow-lg opacity-0 transition-all duration-300 transform mobile-button sm:w-auto sm:px-8 sm:py-4 hover:from-red-700 hover:to-red-600 sm:text-lg hover:shadow-xl hover:-translate-y-1"
                  onClick={() => navigate("/login")}
                >
                  <span className="button-animation">Get Started</span>
                  <ChevronRight className="ml-2 w-5 h-5 button-animation" />
                </button>
              </div>
            </div>
          </div>

          {/* Glassmorphic image section - TALLER VERSION */}
          <div ref={imageRef} className="relative w-full lg:w-1/2">
            {/* Image container with taller, more proportional height */}
            <div className="image-animation opacity-0 h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px] xl:h-[750px]">
              <div className="overflow-hidden relative w-full h-full rounded-xl shadow-xl sm:rounded-2xl">
                {/* Shadow overlay at bottom to enhance depth */}
                <div className="absolute inset-x-0 bottom-0 z-10 h-1/3 bg-gradient-to-t to-transparent from-black/40"></div>

                {/* Placeholder if no image is provided */}
                {!imageUrl && (
                  <div className="flex absolute inset-0 justify-center items-center bg-gradient-to-br from-red-500 to-blue-600">
                    <div className="relative">
                      <MessageSquareText className="w-24 h-24 text-white opacity-20 sm:w-32 md:w-40 sm:h-32 md:h-40" />
                      <div className="flex absolute inset-0 justify-center items-center text-xl font-bold text-white sm:text-2xl">
                        TXTConnect
                      </div>
                    </div>
                  </div>
                )}

                {/* Actual image when provided */}
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt="TXTConnect in action"
                    className="object-cover w-full h-full"
                  />
                )}

                {/* Enhanced glassmorphic elements */}
                {/* Top message element - SMS-focused */}
                <div className="absolute left-6 top-10 z-20 p-4 w-56 h-36 rounded-xl border shadow-lg opacity-0 backdrop-blur-md glass-element sm:top-16 md:top-24 sm:left-10 md:left-16 sm:w-64 md:w-72 sm:h-40 md:h-48 bg-white/20 border-white/30 sm:p-5">
                  <div className="flex items-center mb-3">
                    <div className="flex justify-center items-center mr-3 w-9 h-9 rounded-full sm:w-10 md:w-12 sm:h-10 md:h-12 bg-red-500/90">
                      <MessageSquareText className="w-5 h-5 text-white sm:w-5 md:w-6 sm:h-5 md:h-6" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-white sm:text-base md:text-lg">
                        TXTConnect
                      </h4>
                      <p className="text-xs md:text-sm text-white/80">
                        SMS service
                      </p>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/20">
                    <p className="text-xs text-white sm:text-sm md:text-base">
                      Thank you all for attending the annual office brunch
                    </p>
                  </div>
                </div>

                {/* Message delivery notification */}
                <div className="flex absolute right-8 bottom-16 z-20 items-center p-3 w-48 h-20 rounded-xl border shadow-lg opacity-0 backdrop-blur-md glass-element sm:bottom-20 md:bottom-24 sm:right-12 md:right-16 sm:w-56 md:w-64 sm:h-24 md:h-28 bg-white/20 border-white/30 sm:p-4">
                  <div className="flex justify-center items-center mr-3 w-10 h-10 rounded-full sm:w-12 md:w-14 sm:h-12 md:h-14 bg-blue-500/90">
                    <CheckCircle className="w-5 h-5 text-white sm:w-6 md:w-7 sm:h-6 md:h-7" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white sm:text-base md:text-lg">
                      Message delivered
                    </p>
                    <p className="text-xs md:text-sm text-white/80">
                      to 3,248 recipients
                    </p>
                  </div>
                </div>

                {/* SMS credits notification element */}
                <div className="flex absolute right-10 top-52 z-20 items-center p-3 w-36 h-16 rounded-xl border shadow-lg opacity-0 backdrop-blur-md glass-element sm:top-64 md:top-80 sm:right-12 md:right-20 sm:w-40 md:w-48 sm:h-18 md:h-20 bg-white/20 border-white/30">
                  <div className="flex justify-center items-center mr-2 w-8 h-8 rounded-full sm:w-9 md:w-10 sm:h-9 md:h-10 bg-blue-500/90">
                    <MessageSquareText className="w-4 h-4 text-white sm:w-5 sm:h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-white sm:text-sm">
                      SMS credits
                    </p>
                    <p className="text-xs text-white/80">5,000 remaining</p>
                  </div>
                </div>

                {/* Decorative floating elements */}
                <div className="absolute left-8 bottom-48 z-10 w-12 h-12 rounded-full border shadow-lg opacity-0 backdrop-blur-sm glass-element sm:bottom-56 md:bottom-64 sm:left-10 md:left-12 sm:w-14 md:w-16 sm:h-14 md:h-16 bg-red-400/30 border-white/20"></div>
                <div className="absolute right-28 top-32 z-10 w-10 h-10 rounded-full border shadow-lg opacity-0 backdrop-blur-sm glass-element sm:top-40 md:top-48 sm:right-32 md:right-40 sm:w-12 md:w-14 sm:h-12 md:h-14 bg-blue-400/30 border-white/20"></div>
                <div className="absolute bottom-32 left-32 z-10 w-8 h-8 rounded-full border shadow-lg opacity-0 backdrop-blur-sm glass-element sm:bottom-40 md:bottom-52 sm:left-40 md:left-52 sm:w-10 md:w-12 sm:h-10 md:h-12 bg-purple-400/30 border-white/20"></div>
              </div>
            </div>
          </div>

          {/* Steps content - Desktop only */}
          <div className="hidden lg:block lg:w-1/2">
            <div ref={stepsRef} className="relative pt-6 pl-10 w-full">
              {/* Vertical timeline line */}
              <div className="absolute left-0 top-0 bottom-16 w-0.5 bg-gradient-to-b from-red-400 via-blue-400 to-purple-400 z-0 ml-10" />

              {steps.map((step, index) => (
                <div key={step.number} className="relative mb-16 last:mb-0">
                  {/* Step number circle with vertical line connection */}
                  <div className="flex absolute left-0 flex-col items-center -ml-10 transform -translate-x-1/2">
                    <div
                      className={`desktop-step-number z-10 w-16 h-16 rounded-full ${step.bgColor} border-2 ${step.borderColor} flex items-center justify-center shadow-md opacity-0`}
                    >
                      <span className={`text-2xl font-bold ${step.iconColor}`}>
                        {step.number}
                      </span>
                    </div>

                    {index < steps.length - 1 && (
                      <div className="desktop-step-line w-0.5 h-24 bg-gray-200 origin-top mt-2 opacity-0"></div>
                    )}
                  </div>

                  {/* Step content card - Desktop */}
                  <div
                    className={`relative p-6 ml-6 bg-white rounded-xl border border-gray-100 shadow-lg opacity-0 transition-all duration-300 desktop-step-card hover:shadow-xl`}
                  >
                    <div className="flex gap-4 items-start">
                      {/* Icon */}
                      <div
                        className={`flex-shrink-0 p-3 rounded-full ${step.bgColor} ${step.iconColor}`}
                      >
                        {step.icon}
                      </div>

                      <div className="flex-1">
                        <h3 className="mb-2 text-xl font-bold text-gray-800">
                          {step.title}
                        </h3>

                        <p className="mb-4 text-gray-600">{step.description}</p>

                        {/* Features list - Desktop */}
                        <div className="p-3 space-y-2 bg-gray-50 rounded-lg">
                          {step.features.map((feature, fIndex) => (
                            <div key={fIndex} className="flex items-center">
                              <div
                                className={`w-6 h-6 rounded-full ${step.bgColor} flex items-center justify-center mr-2`}
                              >
                                {feature.icon}
                              </div>
                              <span className="text-sm text-gray-700">
                                {feature.text}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* SMS-only indicator for step 2 - Desktop */}
                        {index === 1 && (
                          <div className="flex mt-4 space-x-2">
                            <div className="flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-full border border-blue-200">
                              <MessageSquareText className="mr-1 w-3 h-3" />
                              SMS
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Arrow connector for desktop */}
                    {index < steps.length - 1 && (
                      <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
                        <ArrowRight className="w-6 h-6 text-gray-300 rotate-90" />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="pl-6 mt-12">
                <button
                  className="flex items-center px-8 py-4 text-lg font-bold text-white bg-gradient-to-r from-red-600 to-red-500 rounded-full shadow-lg opacity-0 transition-all duration-300 transform desktop-step-card hover:from-red-700 hover:to-red-600 hover:shadow-xl hover:-translate-y-1"
                  onClick={() =>
                    animate(
                      ".button-animation",
                      { scale: [0.95, 1] },
                      { duration: 0.2 }
                    )
                  }
                >
                  <span className="button-animation">Get Started</span>
                  <ChevronRight className="ml-2 w-5 h-5 button-animation" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
